import type { IJsonApiResponse } from "@/interfaces/IJsonApiResponse";
import Model from "./Model";
import { Jsona } from "jsona";
import type { TJsonApiBody, TJsonApiData } from "jsona/lib/JsonaTypes";
import type IJsonApiPageMeta from "~/interfaces/IJsonApiPageMeta";
import type { ICustomer } from "./Customer";
import type { IProduct } from "./Product";
import Customer from "./Customer";
import Product from "./Product";

export interface IProductReview {
  id: string | null;
  customer_name: string | null;
  rating: number;
  opinion: string | null;
  short_opinion: string | null;
  created_utc: string | null;
  created_at_text?: string;
  customer?: ICustomer;
  product?: IProduct;
}

export default class ProductReview extends Model implements IProductReview {
  public type: string = "product-reviews";
  public relationshipNames: string[] = ["product", "customer"];

  public id: string | null = null;
  public customer_name: string | null = null;
  public rating: number = 0;
  public opinion: string | null = null;
  public short_opinion: string | null = null;
  public created_utc: string | null = null;
  public created_at_text?: string;
  public customer?: Customer;
  public product?: Product;

  public constructor({
    id,
    customer_name,
    rating,
    opinion,
    short_opinion,
    created_utc,
    created_at_text,
    customer,
    product,
  }: IProductReview) {
    super();

    this.id = id || null;
    this.customer_name = customer_name || null;
    this.rating = rating || 0;
    this.opinion = opinion || null;
    this.short_opinion = short_opinion || null;
    this.created_utc = created_utc || null;
    this.created_at_text = created_at_text;
    this.customer = customer ? new Customer(customer) : undefined;
    this.product = product ? new Product(product) : undefined;
  }

  public static getDefaultQueryFields() {
    return {
      "fields[product-reviews]":
        "customer_name,rating,opinion,short_opinion,created_utc,created_at_text",
    };
  }

  public static async fetchByProduct(productId: string, page: number = 1) {
    const path = `/shop-v1/products/${productId}/reviews`;
    const query = {
      "page[number]": page.toString(),
      "page[size]": "5",
      sort: "-id",
      ...ProductReview.getDefaultQueryFields(),
    };
    const queryStr = new URLSearchParams(query).toString();
    const url = path + "?" + queryStr;
    const { status, data, error } = await useApiFetch(url);
    if (status.value != "success") {
      throw data.value || error.value;
    }
    const jsona = new Jsona();
    const jsonapi = jsona.deserialize(data.value as TJsonApiBody);
    const instances = ProductReview.fromArray(jsonapi as IProductReview[]);
    const meta = (data.value as IJsonApiResponse)?.meta as
      | IJsonApiPageMeta
      | undefined;
    return { data: instances, meta };
  }

  public async save() {
    const path = `/shop-v1/product-reviews` + (this.id ? `/${this.id}` : "");
    const query = {
      ...ProductReview.getDefaultQueryFields(),
    };
    const queryStr = new URLSearchParams(query).toString();
    const url = path + "?" + queryStr;
    const jsona = new Jsona();
    const input = jsona.serialize({ stuff: this });
    if (!this.id) {
      delete (input.data as TJsonApiData).id;
    }
    const { status, data, error } = await useApiFetch(url, {
      method: this.id ? "PATCH" : "POST",
      body: JSON.stringify(input),
      server: false,
      immediate: true,
    });
    if (status.value != "success") {
      throw data.value || error.value;
    }
    const doc = jsona.deserialize(data.value as TJsonApiBody) as IProductReview;
    Object.assign(this, new ProductReview(doc));
  }

  public static fromArray(items: IProductReview[]): ProductReview[] {
    return items.map((item: IProductReview) => new ProductReview(item));
  }

  public getCreatedDate() {
    if (!this.created_utc) {
      return null;
    }
    const date = new Date(this.created_utc);
    if (!(date instanceof Date)) {
      return null;
    }
    const Y = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    return `${Y}-${m}-${d}`;
  }
}
